import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { changeLanguage as persistLanguageChange } from '../utils/languageUtils';
import { useUnsavedChangesState } from '../context/UnsavedChangesContext';

/**
 * Single entry point for switching the app language (UAT-CS-01). Centralizes what
 * used to be four separate copy-pasted toggle implementations (Header, login,
 * Register, and useLanguage's setLanguage): always confirms the switch with the
 * user first, then defers to persistLanguageChange for the actual i18next call +
 * storage write (the same one AutoTenantLanguage reads to know a user has an
 * explicit preference — do not bypass it with a second storage key).
 * document.documentElement.lang/dir are no longer set here; i18n.js's own
 * languageChanged listener is now the single place that happens, so it's correct
 * on first load too, not only on a manual switch.
 *
 * The confirmation is unconditional (not just when isDirty) for two reasons:
 * the product wants an explicit "are you sure?" step before every switch, and
 * committing the switch only after the picker has already closed avoids a race
 * where the RTL/LTR direction flip reflows the page while the dropdown's
 * positioning library is still mid-close, which rendered the menu clipped/
 * garbled (visible as truncated option labels right after picking a language).
 */
export const useChangeLanguage = () => {
  const { i18n } = useTranslation();
  const { isDirty } = useUnsavedChangesState();
  const [pendingLanguage, setPendingLanguage] = useState<string | null>(null);
  // Captured at request time so the confirmation copy can still call out unsaved
  // changes specifically, even though the modal now always appears.
  const [pendingWasDirty, setPendingWasDirty] = useState(false);

  const commit = useCallback((lng: string) => {
    persistLanguageChange(lng);
  }, []);

  const requestChangeLanguage = useCallback(
    (lng: string) => {
      const current = i18n.resolvedLanguage || i18n.language;
      if (lng === current) return;

      setPendingWasDirty(isDirty);
      setPendingLanguage(lng);
    },
    [i18n.resolvedLanguage, i18n.language, isDirty],
  );

  const confirmSwitch = useCallback(() => {
    if (pendingLanguage) commit(pendingLanguage);
    setPendingLanguage(null);
    setPendingWasDirty(false);
  }, [pendingLanguage, commit]);

  const cancelSwitch = useCallback(() => {
    setPendingLanguage(null);
    setPendingWasDirty(false);
  }, []);

  return { requestChangeLanguage, pendingLanguage, pendingWasDirty, confirmSwitch, cancelSwitch };
};

export default useChangeLanguage;
